import { Suspense, lazy, useEffect } from "react";
import LegacyToastBridge from "./components/LegacyToastBridge";
import DashboardToaster from "./components/DashboardToaster";
import { TooltipProvider } from "@bitsentry-ce/components/ui/tooltip";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "@bitsentry-ce/components/theme";
import { CacheDebugger } from "@bitsentry/components/dev/CacheDebugger";
import ErrorBoundary from "@bitsentry/components/ErrorBoundary";
import { ProtectedRoute } from "@bitsentry/components/auth/ProtectedRoute";
import { RoleRoute } from "@bitsentry/components/auth/RoleRoute";
import { OfflineIndicator } from "@bitsentry/components/ui/offline-indicator";
import { AuthProvider } from "./platform/auth/provider";
import { useAuthSession } from "@bitsentry-ce/components/services/hooks";
import { WebSocketProvider } from "./platform/websocket/provider";
import { useOfflineSync } from "./hooks/useOfflineSync";
import { queryClient } from "./platform/query/client";
import { TRPCProvider } from "./platform/trpc/provider";
import { FrontendBitsentryServicesProvider } from "./platform/services-adapter";
const DashboardShell = lazy(() => import("./components/DashboardShell"));
const PlatformTourAutoStart = lazy(
  () => import("./components/PlatformTourAutoStart"),
);
import PageTitleUpdater from "./components/PageTitleUpdater";
import { capturePageview } from "./posthog";
const Results = lazy(() => import("./pages/Results"));
const TourDashboardPreview = lazy(
  () =>
    import("@bitsentry-ce/components/tutorial/previews/TourDashboardPreview"),
);
const TourIncidentsPreview = lazy(
  () =>
    import("@bitsentry-ce/components/tutorial/previews/TourIncidentsPreview"),
);
const TourRunbooksPreview = lazy(
  () =>
    import("@bitsentry-ce/components/tutorial/previews/TourRunbooksPreview"),
);
const TourResultsPreview = lazy(
  () => import("@bitsentry-ce/components/tutorial/previews/TourResultsPreview"),
);
import { useTranslation } from "@bitsentry-ce/i18n";

const Index = lazy(() => import("./pages/Index"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Tickets = lazy(() => import("./pages/Tickets"));
const ResolutionManagement = lazy(() => import("./pages/ResolutionManagement"));
const Incidents = lazy(() => import("./pages/Incidents"));
const WorkosCallback = lazy(async () => ({
  default: (await import("./pages/auth/WorkosCallback")).WorkosCallback,
}));
const Profile = lazy(() => import("./pages/Profile"));
const PilotMetrics = lazy(() => import("./pages/PilotMetrics"));
const Runbooks = lazy(() => import("./pages/Runbooks"));
const Login = lazy(async () => ({
  default: (await import("./pages/auth/Login")).Login,
}));
const ResetPassword = lazy(async () => ({
  default: (await import("./pages/auth/ResetPassword")).ResetPassword,
}));
const EditUser = lazy(async () => ({
  default: (await import("./pages/settings/EditUser")).EditUser,
}));

const RouteLoading = () => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
      {t("navigation.app.loading")}
    </div>
  );
};

const PostHogRouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    capturePageview(location.pathname);
  }, [location.pathname]);

  return null;
};

const AppContent = () => {
  const { isAuthenticated } = useAuthSession();
  useOfflineSync();

  return (
    <>
      <OfflineIndicator />
      {import.meta.env.VITE_ENV === "development" && <CacheDebugger />}
      <LegacyToastBridge />
      <DashboardToaster />
      <BrowserRouter>
        <PageTitleUpdater />
        <PostHogRouteTracker />
        {isAuthenticated && (
          <Suspense fallback={null}>
            <PlatformTourAutoStart />
          </Suspense>
        )}
        <Suspense fallback={<RouteLoading />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route
              path="/forgot-password"
              element={<Navigate to="/login" replace />}
            />
            <Route
              path="/reset-password"
              element={<Navigate to="/auth/reset-password" replace />}
            />
            <Route
              path="/confirm-email"
              element={<Navigate to="/login" replace />}
            />
            <Route
              path="/magic-link/verify"
              element={<Navigate to="/login" replace />}
            />
            <Route path="/auth/workos/callback" element={<WorkosCallback />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            {/* Protected routes share one persistent dashboard shell. */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardShell />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Navigate to="/diagnosis" replace />} />
              <Route path="/diagnosis" element={<Index />} />
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/resolution" element={<ResolutionManagement />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/incidents" element={<Incidents />} />
              <Route path="/runbooks" element={<Runbooks />} />
              <Route
                path="/pilot-metrics"
                element={
                  <RoleRoute allowedRoles={[1]}>
                    <PilotMetrics />
                  </RoleRoute>
                }
              />
              <Route path="/results" element={<Results />} />
              <Route path="/investigations" element={<Results />} />
              <Route
                path="/settings"
                element={
                  <RoleRoute allowedRoles={[1]}>
                    <Settings />
                  </RoleRoute>
                }
              />
              <Route
                path="/settings/edit-user/:userId"
                element={
                  <RoleRoute allowedRoles={[1]}>
                    <EditUser />
                  </RoleRoute>
                }
              />
              <Route path="/profile" element={<Profile />} />
            </Route>
            {/* Tour preview routes */}
            <Route path="/tour/dashboard" element={<TourDashboardPreview />} />
            <Route path="/tour/incidents" element={<TourIncidentsPreview />} />
            <Route path="/tour/runbooks" element={<TourRunbooksPreview />} />
            <Route path="/tour/results" element={<TourResultsPreview />} />

            {/* 404 route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <ThemeProvider>
      <TooltipProvider>
        <TRPCProvider queryClient={queryClient}>
          <AuthProvider>
            <WebSocketProvider>
              <FrontendBitsentryServicesProvider>
                <AppContent />
              </FrontendBitsentryServicesProvider>
            </WebSocketProvider>
          </AuthProvider>
        </TRPCProvider>
      </TooltipProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
